import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { delay, retryWhen, take } from 'rxjs/operators';
import { QueryParamsModel } from 'src/app/shared/base/models';
import { environment as env } from 'src/environments/environment';
import { CreateUserRequest, ResetPasswordRequest, SearchResult, UpdateUserRequest, User, UserProjectSummary } from '../../models';

const API: any = {
  users: 'users',
  resetPassword: 'reset-tempory-password',
  restoreUser: 'restore-user',
  liveProjectCount: 'live-project-count',
  projectSummary: 'project-summary',
  reassignProjects: 'reassign-projects'
};

@Injectable({ providedIn: 'root' })
export class UsersService {
  constructor(private readonly http: HttpClient) { }

  getUsers(query: QueryParamsModel): Observable<SearchResult<User>> {
    return this.http
      .get<any>(env.apiUrl + API.users + query.toUrlParams())
      .pipe(retryWhen((errors) => errors.pipe(delay(1000), take(10000))));
  }

  getUser(id: number): Observable<any> {
    return this.http
      .get<any>(`${env.apiUrl}${API.users}/${id}`);
  }

  createUser(request: CreateUserRequest): Observable<any> {
    return this.http.post<any>(
      `${env.apiUrl}${API.users}`,
      request
    );
  }

  updateUser(request: UpdateUserRequest): Observable<any> {
    return this.http.put<any>(
      `${env.apiUrl}${API.users}/${request.id}`,
      request
    );
  }

  getLiveProjectCount(userId: number): Observable<number> {
    return this.http.get<number>(
      `${env.apiUrl}${API.users}/${userId}/${API.liveProjectCount}`
    );
  }

  // Live, completed and reassignable counts in one call. Prefer this over
  // getLiveProjectCount anywhere the admin has to make a decision, not just be warned.
  getProjectSummary(userId: number): Observable<UserProjectSummary> {
    return this.http.get<UserProjectSummary>(
      `${env.apiUrl}${API.users}/${userId}/${API.projectSummary}`
    );
  }

  // Hands every live project the user owns as CSR or supervisor to targetUserId and
  // returns how many actually moved — fewer than reassignableProjectCount when a
  // CSR-owned project sits in a status the CSA transfer rules forbid.
  reassignProjects(userId: number, targetUserId: number): Observable<number> {
    return this.http.put<number>(
      `${env.apiUrl}${API.users}/${userId}/${API.reassignProjects}`,
      { targetUserId }
    );
  }

  // acknowledgeLiveProjects is the caller confirming it showed the count from
  // getLiveProjectCount and the admin said yes anyway; the API refuses the
  // deactivation without it when the user still has live projects.
  deactivateUser(userId: number, acknowledgeLiveProjects = false): Observable<any> {
    return this.http.delete<any>(
      `${env.apiUrl}${API.users}/${userId}?acknowledgeLiveProjects=${acknowledgeLiveProjects}`
    );
  }

  activateUser(userId: number): Observable<any> {
    return this.http.put<any>(
      `${env.apiUrl}${API.users}/${userId}/${API.restoreUser}`, ''
    );
  }

  resetPassword(request: ResetPasswordRequest): Observable<any> {
    return this.http.post<any>(
      `${env.apiUrl}${API.users}/${API.resetPassword}`,
      request
    );
  }
}
